import { RouterType } from '@moonpig/web-core-routing'
import {
  ApplicableFilter,
  FilterItem,
  SearchFilter,
} from '../../services/types/services'
import { FilterAction, FilterSource } from '../../types'
import { QueryFilters, StateIntegerFilter } from '../slices/filters/types'
import { updateRoute } from './updateRoute'
import { QueryProductsProps } from '../slices/products'
import { getIntegerFilterInput } from './getIntegerFilterInput'

const clearIntegerFilterSelection = (
  filters: FilterItem[],
  integerId?: string,
): FilterItem[] =>
  filters.map(filterItem => {
    if (filterItem.__typename !== 'IntegerFilter') {
      return filterItem
    }

    if (integerId && filterItem.integerId !== integerId) {
      return filterItem
    }

    return {
      ...filterItem,
      selectedValue: null,
      selectedValueLabel: null,
      isSelected: false,
      integerKey: undefined,
    }
  })

type Props = {
  preAppliedFilters: SearchFilter[]
  selectedFilters: ApplicableFilter[]
  queryFilters: QueryFilters
  integerFilters: StateIntegerFilter[]
  filter?: ApplicableFilter
  router: RouterType | null
  searchContext?: {
    searchTerm: string
    selectedSuggestion?: { label: string }
  }
  queryProducts?: (searchTypeProps: QueryProductsProps) => void
  filteringEvent: {
    action: FilterAction
    filter?: {
      id: string
      parent: string
    }
    source: FilterSource
  }
  hasSuggestionBeenRemoved?: boolean
  usePageNavigation?: boolean
}

export const clearAndUpdateFilters = async ({
  preAppliedFilters,
  selectedFilters,
  queryFilters,
  integerFilters,
  filter,
  router,
  searchContext,
  queryProducts,
  filteringEvent,
  hasSuggestionBeenRemoved,
  usePageNavigation = false,
}: Props) => {
  const isClearingAllFilters = !filter
  const isClearingIntegerFilter =
    filter && filter.__typename === 'IntegerFilter'
  const integerFilterInput =
    isClearingAllFilters || isClearingIntegerFilter
      ? undefined
      : getIntegerFilterInput(integerFilters)

  const {
    filters: updatedFiltersFromQuery,
    noResultsFilters: noResultsFiltersAfterClear,
    quickFilterKeys: quickFilterKeysAfterClear,
  } = await queryFilters({
    filters: [
      ...preAppliedFilters,
      ...selectedFilters.map(f => ({
        key: f.id,
        group: f.parent,
        userApplied: true,
      })),
    ],
    integerFilter: integerFilterInput,
  })

  const filtersAfterClear =
    isClearingAllFilters || isClearingIntegerFilter
      ? clearIntegerFilterSelection(
          updatedFiltersFromQuery,
          isClearingIntegerFilter ? filter.integerId : undefined,
        )
      : updatedFiltersFromQuery

  let integerFiltersAfterClear = integerFilters
  if (!filter) {
    integerFiltersAfterClear = integerFilters.map(f => ({
      ...f,
      value: null,
    }))
  } else {
    integerFiltersAfterClear =
      filter.__typename === 'IntegerFilter'
        ? integerFilters.map(f =>
            f.integerId === filter.integerId
              ? { ...f, value: null }
              : /* istanbul ignore next */ f,
          )
        : integerFilters
  }

  updateRoute({
    router,
    selectedFilters,
    preAppliedFilters: [],
    mostRecentSearchTerm:
      searchContext?.selectedSuggestion?.label ||
      searchContext?.searchTerm ||
      '',
    usePageNavigation,
  })

  if (noResultsFiltersAfterClear.length === 0 && !hasSuggestionBeenRemoved) {
    await queryProducts?.({
      newPage: 1,
      isLoadMore: false,
      isPrevious: false,
      filters: selectedFilters,
      integerFilter: integerFilterInput,
      filteringEvent,
    })
  }

  return {
    filtersAfterClear,
    integerFiltersAfterClear,
    noResultsFiltersAfterClear,
    quickFilterKeysAfterClear,
  }
}
